import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import PanelResult from './PanelResult';
import InstallationDetail from './InstallationDetail';
import { useIntegrationInstallationsQuery } from '../integrationsApi';
import { apiMessage, formatDateTime, toneForInstallationHealth, toneForInstallationStatus } from '../lib';

const STATUSES = ['PENDING', 'ACTIVE', 'SUSPENDED'] as const;

/**
 * Screen 2 — this venue's own installations: what it has actually connected,
 * its status and its live health. Uninstalled rows are history rather than
 * inventory (`IntegrationsService.findAll`'s own words) and are left out
 * unless asked for, matching the API's default.
 */
export default function InstallationsPanel() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState('');
  const [includeUninstalled, setIncludeUninstalled] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const query = {
    limit: 100,
    ...(status ? { status: status as (typeof STATUSES)[number] } : {}),
    includeUninstalled,
  };
  const { data, isFetching, isError, error, refetch } = useIntegrationInstallationsQuery(query);

  const filtered = Boolean(status);
  const clearFilters = () => setStatus('');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('integrations.installations.filterStatus')} controlId="inst-status">
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-48">
              <option value="">{t('integrations.installations.filterAllStatuses')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`integrations.installationStatus.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          <label className="flex items-center gap-2 self-end pb-2.5 text-sm">
            <input
              type="checkbox"
              checked={includeUninstalled}
              onChange={(e) => setIncludeUninstalled(e.target.checked)}
            />
            {t('integrations.installations.includeUninstalled')}
          </label>
          {filtered && (
            <Button variant="outline" onClick={clearFilters}>
              {t('integrations.states.clearFilters')}
            </Button>
          )}
        </div>
      </div>

      <PanelResult
        loading={isFetching}
        error={isError ? apiMessage(error, t('integrations.states.loadFailed')) : null}
        count={data?.items.length ?? 0}
        onRetry={refetch}
        filtered={filtered}
        onClearFilters={clearFilters}
        emptyTitle={t('integrations.installations.emptyTitle')}
        emptyHint={t('integrations.installations.emptyHint')}
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('integrations.providers.name')}</TH>
                <TH>{t('integrations.common.branch')}</TH>
                <TH>{t('integrations.installations.status')}</TH>
                <TH>{t('integrations.installations.health')}</TH>
                <TH>{t('integrations.installations.lastSuccess')}</TH>
                <TH>{t('integrations.installations.lastFailure')}</TH>
              </TR>
            </THead>
            <tbody>
              {(data?.items ?? []).map((installation) => (
                <TR
                  key={installation.id}
                  className="cursor-pointer"
                  onClick={() => setOpenId(installation.id)}
                >
                  <TD>
                    <p className="font-medium">
                      {installation.provider?.name ?? installation.providerKey}
                    </p>
                    <p className="text-xs text-ink-muted">{installation.providerKey}</p>
                  </TD>
                  <TD className="text-sm text-ink-muted">
                    {installation.branch?.name ?? t('integrations.providers.wholeVenue')}
                  </TD>
                  <TD>
                    <Badge tone={toneForInstallationStatus(installation.status)}>
                      {t(`integrations.installationStatus.${installation.status}`)}
                    </Badge>
                  </TD>
                  <TD>
                    <Badge tone={toneForInstallationHealth(installation.health)}>
                      {t(`integrations.health.${installation.health}`)}
                    </Badge>
                  </TD>
                  <TD className="text-sm tabular-nums text-ink-muted">
                    {formatDateTime(installation.lastSuccessAt, i18n.language)}
                  </TD>
                  <TD className="text-sm tabular-nums text-ink-muted">
                    {formatDateTime(installation.lastFailureAt, i18n.language)}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      {openId && <InstallationDetail id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
